import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon } from '@/components/catalog/icons';
import { NotesPanel } from '@/components/notes/NotesPanel';
import { TaskFormModal } from '@/components/tasks/TaskFormModal';
import { TaskList } from '@/components/tasks/TaskList';
import { Badge, Button, Panel, PanelHeader } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { useBodyScrollLock } from '@/lib/use-sidebar';
import type { Task, TaskListParams, TaskState, TaskTargetType } from '@/lib/types';

/*
 * Tasks and notes for one record, in a slide-over.
 *
 * One deliberate non-decision:
 *  - **Not tabs.** Tasks sit above notes in ONE scroller — the second section
 *    is found by scrolling, not by discovering a tab.
 *
 * Everything is driven off the `target` prop — nothing enumerates target
 * types, so a fifth one works here unchanged.
 *
 * ## Gating: the PERMISSION SET, and never the role
 *
 * 🔴 **CORRECTED 2026-09-04.** *SUPERSEDED: "**Not role-gated.** The task and
 * note routers carry no role guard, so a `viewer` may add both. Nothing here
 * consults `ability`."* **The premise was true and the conclusion did not
 * follow.** `tasks` and `notes` are two of the THREE modules this API genuinely
 * enforces through permission sets — `OPEN_WRITE_MODULES` in `permissions.ts` —
 * so the absence of a `requireRole` is precisely what hands the decision to the
 * set, WITH a scope that really narrows. A person whose set omits
 * `tasks.create` had "Add task" offered to them on every deal, quote, invoice,
 * deliverable and company row, and the write answered 403.
 *
 * ⚠️ **The four grants are asked SEPARATELY and must stay that way** — a set can
 * grant `notes.create` and refuse `tasks.create`, so one shared flag would be
 * wrong in one direction or the other on every record.
 *
 * ⚠️ **Reading is a different question from writing.** The drawer, the rows and
 * the notes below them render for everybody: its whole job is showing what is
 * there. Only the CREATE, UPDATE and DELETE affordances are gated.
 *
 * ⚠️ **Disabled with the reason beside it, never removed.** A control that
 * vanishes is indistinguishable from a feature that was taken away — the
 * refusal here is about THIS reader, not about the system, so it is named.
 * (Contrast Xero's `sync`, where the affordance is absent because the only
 * outcome is an error for everybody.)
 *
 * ⚠️ **The gate is the optimisation; the 403 is the guarantee.** A grant can be
 * withdrawn under a live access token, so every write below still handles the
 * refusal where it arrives — a 403 body carries no `details`, so it renders as
 * a banner and never as a field error.
 */

/* --- target plumbing ----------------------------------------------------- */

export interface ActivityTarget {
  type: TaskTargetType;
  id: number;
}

/**
 * The list-filter key for a target type — `deal` filters on `dealId`.
 *
 * A template literal rather than a hand-written `Record`, so a target type
 * added to the union needs no edit here. The `Pick` stops this compiling if
 * the union gains a member `TaskListParams` has no filter for, instead of
 * quietly sending a key the strict query 422s on.
 */
type TaskFilterKey = `${TaskTargetType}Id`;

function taskFilterFor(target: ActivityTarget): Pick<TaskListParams, TaskFilterKey> {
  const key: TaskFilterKey = `${target.type}Id`;
  // A computed key of union type widens to a string index signature, so the
  // assembled object is narrowed back through `key`'s own type.
  return { [key]: target.id } as Pick<TaskListParams, TaskFilterKey>;
}

/* --- change notifications ------------------------------------------------ */

/*
 * The trigger's counts are fetched by the button but invalidated by the
 * drawer — add a task in the sheet and the badge behind it is wrong the
 * moment you close. Rather than widen `ActivityButtonProps` with a refresh
 * token every call site would thread through, the two halves talk directly.
 * Keyed by record, so a page showing several triggers only refetches the one
 * that changed.
 */
const changeListeners = new Map<string, Set<() => void>>();

function subscribeToActivity(key: string, listener: () => void): () => void {
  const existing = changeListeners.get(key);
  const set = existing ?? new Set<() => void>();
  set.add(listener);
  if (!existing) changeListeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) changeListeners.delete(key);
  };
}

function announceActivityChange(key: string): void {
  const set = changeListeners.get(key);
  if (!set) return;
  // Copied first: a listener that unsubscribes as it runs would otherwise
  // mutate the set being iterated.
  for (const listener of [...set]) listener();
}

/* --- messages ------------------------------------------------------------ */

function loadMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) return 'You do not have permission to read tasks on this record.';
    if (cause.status === 404) return 'This record no longer exists.';
    return cause.message;
  }
  return 'Could not load the tasks. Check your connection and try again.';
}

/* --- icons --------------------------------------------------------------- */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function CloseIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/*
 * Exported so a row-level control elsewhere can use the SAME mark as the
 * header button. Two different icons opening the same sheet would read as two
 * different features.
 */
export function ActivityIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M9 4h7a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1" />
      <path d="M9 3h4v3H9z" />
      <path d="m7.5 12 1.5 1.5L12 11" />
      <path d="M14.5 16H9" />
    </svg>
  );
}

/**
 * Overdue, inside a badge whose only other content is a number.
 *
 * Smaller than the shared `AlertIcon`, which is sized for body text and swamps
 * a `text-2xs` pill. Exists so overdue is never carried by the badge's colour
 * alone — see the same rule in `TaskList`.
 */
function OverdueGlyph() {
  return (
    <svg {...stroke} strokeWidth={2} className="h-3 w-3 shrink-0">
      <path d="M12 8v4.5" />
      <path d="M12 16.5h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

/* --- the trigger --------------------------------------------------------- */

interface CountSlot {
  key: string;
  open: number;
  overdue: number;
  /**
   * `NoteSummaryRow.count` — the notes PEOPLE wrote, and **viewer-scoped**:
   * without the notes audit grant it counts only this reader's own. So `0` here
   * is *you have written none*, never *nobody has*.
   */
  notes: number;
  /**
   * `NoteSummaryRow.systemCount` — the notes **SEED EMERGE wrote itself**,
   * disjoint from `notes` above and NOT narrowed by authorship the way that one
   * is, because a system note has no author and is visible to every reader who
   * may read notes at all.
   *
   * ⛔ **NEVER DERIVED BY SUBTRACTING, in either direction.** The standing rule
   * at `Note.system` and `NoteSummaryRow.systemCount`: the human count is
   * viewer-scoped and `GET /notes` renders system notes to everybody, so
   * subtracting one from the other is arithmetic across two different
   * visibility rules and comes out wrong by however many notes a colleague
   * happens to have written — with a `200` and nothing failing. It arrives from
   * the server or it is not shown.
   *
   * ⚠️ **Why this field exists at all**: the trigger's counts come from
   * `/notes/summary`, which stopped counting system notes, while the panel it
   * opens lists them — so the button could read `2 notes` over a panel holding
   * three rows, one of them badged `Automatic`, with nothing erroring.
   */
  systemNotes: number;
}

export interface ActivityButtonProps {
  target: ActivityTarget;
  onOpen: () => void;
}

/**
 * The one control that opens the drawer, carrying what is behind it.
 *
 * A count is why this is not a bare "Activity" link: `3` and `5` tell you
 * whether opening it is worth doing.
 *
 * Zeroes are not badged. **The summary endpoints omit records with nothing**
 * rather than zero-filling them, so a missing row means none — every read below
 * defaults to 0. `ids` is capped at 200 server-side; one record is one id, and
 * nothing here loops, so that ceiling is a non-issue for the next caller.
 */
export function ActivityButton({ target, onOpen }: ActivityButtonProps): ReactElement {
  const targetType = target.type;
  const targetId = target.id;
  const targetKey = `${targetType}:${targetId}`;

  const [slot, setSlot] = useState<CountSlot | null>(null);
  const [changeToken, setChangeToken] = useState(0);

  // Refetch when the drawer reports a write against this record. The state
  // change happens in the subscriber, never in the effect body.
  useEffect(
    () => subscribeToActivity(targetKey, () => setChangeToken((token) => token + 1)),
    [targetKey],
  );

  useEffect(() => {
    const controller = new AbortController();
    const key = `${targetType}:${targetId}`;
    const params = { target: targetType, ids: String(targetId) };

    Promise.all([
      api.taskSummary(params, controller.signal),
      api.noteSummary(params, controller.signal),
    ])
      .then(([tasks, notes]) => {
        if (controller.signal.aborted) return;
        const taskRow = tasks.data[0];
        const noteRow = notes.data[0];
        /*
         * ⚠️ **THE ONE COALESCE, AND IT IS AT THE BORDER DELIBERATELY.**
         *
         * `systemCount` is REQUIRED on `NoteSummaryRow` and is **not on the
         * wire yet** — the backend half is in flight — so today this really
         * reads `undefined` however the type is declared. That is exactly the
         * reason the type is required rather than optional: a stray coalesce
         * fails HERE, at the one hop that talks to the server, instead of
         * rendering a confident wrong answer deep in a component.
         *
         * `0` is the honest degradation and not a fudge: the title below says
         * nothing extra at zero, so until the server ships the field the button
         * reads byte-for-byte what it reads today.
         *
         * The `typeof` guard is not belt-and-braces over `??` — `??` catches
         * `undefined` and `null` and would pass a `NaN` or a string straight
         * through into a rendered figure.
         */
        const systemRaw = noteRow?.systemCount;
        setSlot({
          key,
          open: taskRow?.open ?? 0,
          overdue: taskRow?.overdue ?? 0,
          notes: noteRow?.count ?? 0,
          systemNotes:
            typeof systemRaw === 'number' && Number.isFinite(systemRaw) && systemRaw > 0
              ? systemRaw
              : 0,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Counts are decoration on a control that works without them. A
        // failure leaves the button unadorned rather than raising an error
        // the user can do nothing about — the drawer reports the real
        // problem once they open it.
        setSlot(null);
      });

    return () => controller.abort();
  }, [targetType, targetId, changeToken]);

  // Derived during render, so a target change reads as "unknown" without an
  // effect writing state on the way in.
  const counts = slot?.key === targetKey ? slot : null;

  /*
   * The two note figures are named SEPARATELY and never summed.
   *
   * They are disjoint populations under different visibility rules — see
   * `CountSlot.systemNotes` — so one total would be a figure neither endpoint
   * answers for, and the badge beneath still shows the human count alone.
   *
   * ⚠️ **Zero says nothing extra**, so the ordinary record (and every record at
   * all, until the backend ships the field) reads exactly as it does today. A
   * trailing "· 0 automatic" is noise on every row that has never had one.
   *
   * ⛔ This is a LABEL — figures and their nouns, nothing else. The
   * no-helper-text rule holds here as everywhere: a sentence explaining what
   * "automatic" means does not belong on a control.
   */
  const summary = counts
    ? `${pluralise(counts.open, 'open task')}${
        counts.overdue > 0 ? ` (${counts.overdue} overdue)` : ''
      } · ${pluralise(counts.notes, 'note')}${
        counts.systemNotes > 0 ? ` · ${counts.systemNotes} automatic` : ''
      }`
    : undefined;

  return (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={<ActivityIcon />}
      onClick={onOpen}
      {...(summary ? { title: summary } : {})}
    >
      <span>Tasks &amp; notes</span>
      {counts && counts.open > 0 && (
        <Badge
          tone={counts.overdue > 0 ? 'danger' : 'neutral'}
          {...(counts.overdue > 0 ? { icon: <OverdueGlyph /> } : {})}
        >
          <span className="tabular">{counts.open}</span>
          <span className="sr-only">
            {' '}
            open {counts.open === 1 ? 'task' : 'tasks'}
            {counts.overdue > 0 ? `, ${counts.overdue} overdue` : ''}
          </span>
        </Badge>
      )}
      {counts && counts.notes > 0 && (
        <Badge tone="neutral" variant="outline">
          <span className="tabular">{counts.notes}</span>
          <span className="sr-only"> {counts.notes === 1 ? 'note' : 'notes'}</span>
        </Badge>
      )}
    </Button>
  );
}

/* --- the drawer ---------------------------------------------------------- */

/*
 * Same selector and the same trap as `Modal`.
 *
 * Duplicated rather than shared because `Modal` is a centred dialog to its
 * bones — a right-hand sheet cannot be expressed through it without changing
 * that file. The behaviour is copied line for line on purpose: two focus
 * traps that disagree is worse than one selector written twice.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Honours the OS setting, since both jumps below are user-initiated scrolls. */
function scrollBehaviour(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

interface TaskSlot {
  key: string;
  token: number;
  tasks: Task[] | null;
  total: number | null;
  error: string | null;
}

export interface ActivityDrawerProps {
  open: boolean;
  onClose: () => void;
  target: ActivityTarget;
  /** For the heading, so the drawer says what it is about. */
  recordLabel: string;
  viewerId: number | null;
  viewerTimeZone: string;
  /**
   * `'notes'` opens the drawer on its notes: once both sections have loaded,
   * the notes are scrolled to the top of the drawer's own scroller and focus
   * moves to the Notes heading. Applied on every open, and again if the target
   * changes while open. Omitted, the drawer opens exactly as it always has.
   *
   * ⚠️ **It waits for the TASKS too**, not only the notes: tasks sit above the
   * notes in one scroller, so scrolling before they land would leave the notes
   * pushed back down by however tall the task list turned out to be.
   */
  focus?: 'notes';
  /**
   * Fires whenever a write made in this drawer is ACCEPTED by the server — a
   * task created, edited, completed, reopened or deleted, or a note created,
   * edited or deleted — so the page that opened it can refresh its row.
   * Never on a refusal (a 404 on a task included: nothing was written).
   * Optional; omitted, the drawer behaves exactly as it always has.
   */
  onActivityChanged?: () => void;
}

export function ActivityDrawer({
  open,
  onClose,
  target,
  recordLabel,
  viewerId,
  viewerTimeZone,
  focus,
  onActivityChanged,
}: ActivityDrawerProps): ReactElement | null {
  const titleId = useId();
  const descId = useId();
  const tasksHeadingId = useId();

  /*
   * Self-gating, like the inline field components on the revenue documents:
   * the drawer is mounted UNCONDITIONALLY by its callers and decides for
   * itself what it may offer. `{canCreate && <ActivityDrawer/>}` at a call
   * site would take the READING away from somebody who is merely not allowed
   * to write, which is the one thing this panel exists to do.
   *
   * Four separate questions, deliberately. See the header — a set can grant
   * one and refuse its neighbour, so there is no single "may write here".
   */
  const { ability } = useAuth();
  const canCreateTask = ability.canCreate('tasks');
  const canCreateNote = ability.canCreate('notes');
  const canUpdateTask = ability.canUpdate('tasks');
  const canDeleteTask = ability.canDelete('tasks');

  /*
   * Why a control is disabled — one of the four sentences the no-helper-text
   * prohibition explicitly protects, because a bare `disabled` makes a
   * permission denial and a broken button identical.
   *
   * `denialReason` returns null exactly when the action is allowed, so the
   * pair below can never both be non-null with the button still enabled. The
   * task sentence leads when both are refused: it is the control on the left,
   * and both sentences are the same wording in every case a set can produce.
   */
  const createTaskDenial = canCreateTask ? null : denialReason(ability, 'create', 'tasks');
  const createNoteDenial = canCreateNote ? null : denialReason(ability, 'create', 'notes');
  const createDenial = createTaskDenial ?? createNoteDenial;
  const updateTaskDenial = canUpdateTask ? null : denialReason(ability, 'update', 'tasks');

  const panelRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const targetType = target.type;
  const targetId = target.id;
  const targetKey = `${targetType}:${targetId}`;

  /*
   * Stamped with the record it describes AND the token it was fetched under.
   * The key stops one record's tasks flashing under another's when the
   * drawer reopens on a different row. The token makes "a refetch is in
   * flight" a value DERIVED during render (`slot.token !== token`) rather
   * than a flag set inside the effect, which `react(set-state-in-effect)`
   * forbids and which strands a dimmed list when the target changes mid-load.
   */
  const [slot, setSlot] = useState<TaskSlot | null>(null);
  const [token, setToken] = useState(0);
  const [taskForm, setTaskForm] = useState<{ task?: Task } | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const taskFormOpen = taskForm !== null;

  useBodyScrollLock(open);

  /*
   * Focus goes into the sheet on open and back to the trigger on close. The
   * panel itself takes it, rather than the first control, so the dialog's
   * name and description are announced before anything else.
   */
  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => returnFocusTo.current?.focus();
  }, [open]);

  /*
   * Put focus back when the note composer drops it.
   *
   * After adding a note, focus is left on `<body>` — the composer's controls
   * re-render out from under it and nothing catches it. `onKeyDown` below is
   * a REACT handler on the panel, so with focus on the body it never sees
   * keystrokes: Escape stops closing the sheet and Tab walks into the page
   * behind instead of cycling the ring.
   *
   * ⚠️ MUST be `focusout`, not `focusin`. When the focused element is
   * removed, focus lands on the body and `focusout` fires, but **no
   * `focusin` follows** — the body does not "receive" focus, so a listener
   * for it never runs.
   *
   * The check is deferred a tick because during `focusout` the active
   * element is still the one LOSING focus, so testing it inline is always
   * false.
   *
   * The condition is deliberately `body` and nothing else — a general "focus
   * left the panel, pull it back" rule would fight the nested `TaskFormModal`
   * and row-level `ConfirmDialog`, which PORTAL out of this subtree and are
   * legitimately focused while open. The body is never a legitimate place
   * for focus to sit while a dialog is up.
   */
  useEffect(() => {
    if (!open) return;
    let pending = 0;
    function recover() {
      window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        if (document.activeElement === document.body) panelRef.current?.focus();
      }, 0);
    }
    document.addEventListener('focusout', recover);
    return () => {
      // Without clearing this, a focusout fired while the sheet is closing
      // would land after the trigger gets focus back and yank it into a
      // panel that is no longer on screen.
      window.clearTimeout(pending);
      document.removeEventListener('focusout', recover);
    };
  }, [open]);

  /* Tasks. `NotesPanel` fetches its own; this half is ours. */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const key = `${targetType}:${targetId}`;
    const stamp = token;

    // Nothing is written before the request goes out — loading and
    // refetching are both derived below, so this effect only sets state once
    // an answer (or a failure) is in hand. `perPage` is capped at 100
    // server-side; the footer says so rather than passing a page off as the
    // whole list.
    api
      .listTasks(
        { ...taskFilterFor({ type: targetType, id: targetId }), perPage: 100 },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setSlot({ key, token: stamp, tasks: result.data, total: result.meta.total, error: null });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSlot({ key, token: stamp, tasks: null, total: null, error: loadMessage(cause) });
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setActionError(null);
      });

    return () => controller.abort();
  }, [open, targetType, targetId, token]);

  /*
   * `focus="notes"`: put the notes on screen once everything above them has
   * settled.
   *
   * `NotesPanel` fetches its own rows and reports back to nobody, so its load
   * is observed rather than asked about: its `PanelHeader` renders the
   * description paragraph under the heading only once loading has finished
   * (loaded, empty or failed alike), so a heading with a following sibling IS
   * the settled state. A `MutationObserver` waits for it.
   *
   * Recorded per target in a ref and cleared on close, so it fires once per
   * open — not again every time a note is added and the panel re-renders.
   */
  const notesFocusedFor = useRef<string | null>(null);
  const tasksSettled = slot?.key === targetKey;

  useEffect(() => {
    if (open) return;
    notesFocusedFor.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || focus !== 'notes' || !tasksSettled) return;
    if (notesFocusedFor.current === targetKey) return;
    const host = notesRef.current;
    if (!host) return;

    const tryFocus = (): boolean => {
      const heading = host.querySelector<HTMLElement>('#notes-panel-heading');
      if (!heading || heading.nextElementSibling === null) return false;
      notesFocusedFor.current = targetKey;
      // A heading is not focusable on its own; -1 makes it a programmatic
      // target without adding it to the Tab ring.
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      host.scrollIntoView({ block: 'start', behavior: scrollBehaviour() });
      heading.focus({ preventScroll: true });
      return true;
    };

    if (tryFocus()) return;
    const observer = new MutationObserver(() => {
      if (tryFocus()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, focus, targetKey, tasksSettled]);

  /** Replaces a row, but only while the slot still describes this record. */
  const patchTasks = useCallback(
    (change: (tasks: Task[], total: number | null) => { tasks: Task[]; total: number | null }) => {
      setSlot((current) => {
        if (!current || current.key !== targetKey || current.tasks === null) return current;
        const next = change(current.tasks, current.total);
        return { ...current, tasks: next.tasks, total: next.total };
      });
    },
    [targetKey],
  );

  const refresh = useCallback(() => setToken((value) => value + 1), []);

  /*
   * The trigger's counts refresh when the sheet CLOSES, not on each write —
   * the badge is behind the scrim the whole time the sheet is up, so nobody
   * can read a stale count they cannot see. It also catches the half this
   * component cannot observe: `NotesPanel` owns its own adds and deletes and
   * reports back to nobody, so a note count can only be corrected at the
   * boundary.
   */
  useEffect(() => {
    if (!open) return;
    return () => announceActivityChange(targetKey);
  }, [open, targetKey]);

  /*
   * Completion is stamped SERVER-SIDE: `{ state }` is the entire payload, the
   * response carries `completedBy` and `completedAt`, and re-opening clears
   * both. Sending either of them is a 422.
   */
  async function setTaskState(task: Task, next: TaskState) {
    setBusyTaskId(task.id);
    setActionError(null);
    try {
      const saved = await api.updateTask(task.id, { state: next });
      patchTasks((tasks, total) => ({
        tasks: tasks.map((row) => (row.id === saved.data.id ? saved.data : row)),
        total,
      }));
      onActivityChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        patchTasks((tasks, total) => ({
          tasks: tasks.filter((row) => row.id !== task.id),
          total: total === null ? null : Math.max(0, total - 1),
        }));
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not update the task.');
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  /*
   * Only the CREATOR may delete — the assignee gets a 403 telling them to
   * close it instead, because erasing work handed to somebody removes the
   * only trace it was ever asked for. `TaskList` offers the control on
   * `createdBy.id === viewerId`, but an audit holder may also delete, so the
   * refusal is still handled here. A 403 body carries no `details`, so it is
   * a banner, never a field error.
   */
  async function removeTask(task: Task) {
    setBusyTaskId(task.id);
    setActionError(null);
    try {
      await api.deleteTask(task.id);
      patchTasks((tasks, total) => ({
        tasks: tasks.filter((row) => row.id !== task.id),
        total: total === null ? null : Math.max(0, total - 1),
      }));
      onActivityChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        patchTasks((tasks, total) => ({
          tasks: tasks.filter((row) => row.id !== task.id),
          total: total === null ? null : Math.max(0, total - 1),
        }));
      } else if (caught instanceof ApiError && caught.status === 403) {
        setActionError(
          'Only the person who asked for this task can delete it. Close it instead — that keeps the record that it was asked for.',
        );
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not delete the task.');
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  /*
   * "Add note" puts the caret in the composer `NotesPanel` already owns,
   * rather than opening a second one — a scroll and a focus, the same click
   * as reaching for the box directly minus the scrolling.
   */
  function focusNoteComposer() {
    const behavior = scrollBehaviour();
    const box = notesRef.current?.querySelector<HTMLTextAreaElement>(
      'textarea[aria-label="New note"]',
    );
    if (box) {
      box.focus({ preventScroll: true });
      box.scrollIntoView({ block: 'center', behavior });
      return;
    }
    // The composer is absent only while the notes panel is still mounting;
    // putting its section on screen is the honest fallback.
    notesRef.current?.scrollIntoView({ block: 'start', behavior });
  }

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      /*
       * `TaskFormModal` is a React-tree DESCENDANT of this drawer even though
       * it portals elsewhere in the DOM, so its keystrokes bubble here.
       * `Modal` stops Escape and Tab itself; this guard is the second lock,
       * so dismissing the task form can never also dismiss the sheet behind
       * it — and it covers the row-level `ConfirmDialog` too.
       */
      if (taskFormOpen) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      event.stopPropagation();

      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      const first = items.at(0);
      const last = items.at(-1);
      if (!first || !last) return;

      const active = document.activeElement;
      // The panel itself holds focus on open and is not in `items`, so "outside
      // the ring" has to wrap in both directions — otherwise the very first Tab
      // walks straight out into the page behind.
      const inRing = active instanceof HTMLElement && items.includes(active);
      if (event.shiftKey && (active === first || !inRing)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inRing)) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose, taskFormOpen],
  );

  // Rendered nothing when closed rather than moved off-screen: a sheet left
  // mounted keeps its controls in the tab order and its content in the
  // accessibility tree — exactly the problem the trap above solves.
  if (!open) return null;

  const current = slot?.key === targetKey ? slot : null;
  const tasks = current?.tasks ?? null;
  /* `total` — the server's full count — is no longer read here: the "Showing N
     of M tasks" footer it fed was removed on 2026-09-05, so a truncated list
     now reads as the whole list. It is still carried on the slot. */
  const error = current?.error ?? null;
  const loading = current === null;
  const shown = tasks?.length ?? 0;
  const openCount = (tasks ?? []).filter((task) => task.state === 'open').length;
  // Derived, not stored: a slot carrying an older token than the effect is
  // running under IS a refetch in flight. A refetch dims the rows it is
  // about to replace; the first load is the skeleton inside the list.
  const refetching = current !== null && current.token !== token;

  const editingTask = taskForm?.task;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        aria-hidden="true"
        // Inert while the task form is up: a click that dismisses the form must
        // not also take the sheet it was opened from.
        onClick={taskFormOpen ? undefined : onClose}
        className="absolute inset-0 bg-scrim backdrop-blur-[2px]"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        /*
         * A submit inside the nested task form reaches this element through
         * the React tree, not the DOM one. Stopped here as well as at the
         * form — letting it through saves a half-filled record alongside the
         * task.
         */
        onSubmit={(event) => event.stopPropagation()}
        className={cx(
          'relative flex h-full w-full max-w-full flex-col border-l border-border bg-surface shadow-lg',
          // Full width on a phone — at 320px the sheet IS the screen — and a
          // fixed reading width from `sm` up.
          'sm:w-[28rem]',
          'focus:outline-none',
        )}
      >
        <header className="flex items-start gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              Tasks &amp; notes
            </h2>
            <p id={descId} className="mt-0.5 truncate text-xs text-fg-muted" title={recordLabel}>
              {recordLabel}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close tasks and notes"
            leadingIcon={<CloseIcon />}
          />
        </header>

        {/* Both adds live here, outside the scroller, so neither can be below
            the fold on a phone.

            Each is gated on its OWN grant — see the header. Disabled rather
            than removed, with the reason underneath: the refusal is about this
            reader, so a missing button would read as a broken app. */}
        <div className="flex flex-col gap-1.5 border-b border-border bg-surface-sunken px-4 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<PlusIcon />}
              disabled={!canCreateTask}
              onClick={() => setTaskForm({})}
            >
              Add task
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<PlusIcon />}
              disabled={!canCreateNote}
              onClick={focusNoteComposer}
            >
              Add note
            </Button>
          </div>
          {createDenial && <p className="m-0 text-2xs text-fg-muted">{createDenial}</p>}
        </div>

        {/* One scroller for both sections — see the note at the top of the file. */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3">
          <Panel aria-labelledby={tasksHeadingId} className="min-w-0">
            <PanelHeader
              id={tasksHeadingId}
              title="Tasks"
              description={
                loading
                  ? 'Loading…'
                  : error !== null
                    ? 'Couldn’t be loaded'
                    : shown === 0
                      ? 'Nothing is outstanding here'
                      : `${pluralise(shown, 'task')} · ${openCount} still open`
              }
            />

            {actionError && (
              <Notice tone="danger" role="alert" className="mx-4 my-3">
                <span className="min-w-0 [overflow-wrap:anywhere]">{actionError}</span>
              </Notice>
            )}

            <div
              aria-busy={refetching}
              className={
                refetching
                  ? 'opacity-60 transition-opacity duration-150 ease-out motion-reduce:transition-none'
                  : undefined
              }
            >
              {/* The list owns all three of its states — skeleton, empty and
                  error — so every surface showing tasks renders them alike. */}
              <TaskList
                tasks={tasks ?? []}
                showTarget={false}
                onToggleState={(task, next) => void setTaskState(task, next)}
                onEdit={(task) => setTaskForm({ task })}
                onDelete={(task) => void removeTask(task)}
                busyTaskId={busyTaskId}
                viewerTimeZone={viewerTimeZone}
                viewerId={viewerId}
                loading={loading}
                error={
                  error === null ? undefined : (
                    <>
                      {error}{' '}
                      <button
                        type="button"
                        onClick={refresh}
                        className="rounded-xs font-medium underline underline-offset-2"
                      >
                        Try again
                      </button>
                    </>
                  )
                }
                canUpdate={canUpdateTask}
                canDelete={canDeleteTask}
                writeDenialReason={updateTaskDenial}
                // Withheld rather than disabled here, unlike the bar above:
                // this one sits inside an `EmptyState` whose description
                // already says what the panel is for, and a dead call to
                // action under it would be the only thing on screen.
                emptyAction={
                  canCreateTask ? (
                    <Button variant="secondary" size="sm" onClick={() => setTaskForm({})}>
                      Add the first task
                    </Button>
                  ) : undefined
                }
              />
            </div>

          </Panel>

          {/* Self-contained: it fetches, adds, edits and deletes its own rows,
              and brings its own Panel. */}
          <div ref={notesRef} className="min-w-0">
            <NotesPanel
              target={{ type: targetType, id: targetId }}
              viewerId={viewerId}
              {...(onActivityChanged ? { onChanged: onActivityChanged } : {})}
            />
          </div>
        </div>
      </div>

      <TaskFormModal
        open={taskFormOpen}
        onClose={() => setTaskForm(null)}
        task={editingTask}
        target={{ type: targetType, id: targetId }}
        // A refetch rather than a splice: the server decides the order, and a
        // created task has to land where it belongs rather than on top.
        onSaved={() => {
          refresh();
          onActivityChanged?.();
        }}
      />
    </div>,
    document.body,
  );
}
